import type { QueryClient } from '@tanstack/react-query';

/**
 * Invalidates all queries that depend on collections.
 * Call this when collections are created, updated, or deleted.
 */
export function invalidateCollectionCache(queryClient: QueryClient): void {
    queryClient.invalidateQueries({ queryKey: ['collections'] });
    queryClient.invalidateQueries({ queryKey: ['batch-collections'] });
    queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
    queryClient.invalidateQueries({ queryKey: ['edit-form-data'] });
}

/**
 * Invalidates all queries that depend on tags.
 * Call this when tags are created, updated, or deleted.
 */
export function invalidateTagCache(queryClient: QueryClient): void {
    queryClient.invalidateQueries({ queryKey: ['tags'] });
    queryClient.invalidateQueries({ queryKey: ['batch-tags'] });
    queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
    queryClient.invalidateQueries({ queryKey: ['edit-form-data'] });
}

/**
 * Invalidates all queries affected by bulk attribution changes
 * (tag membership replacement, collection moves).
 * Memberships live on ledgers (collectionId/tagIds) and collection
 * responses carry ledgerCount, so both sides must refresh.
 */
export function invalidateAttributionCache(queryClient: QueryClient): void {
    queryClient.invalidateQueries({ queryKey: ['ledgers'] });
    queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
    queryClient.invalidateQueries({ queryKey: ['tags'] });
    queryClient.invalidateQueries({ queryKey: ['collections'] });
    queryClient.invalidateQueries({ queryKey: ['ledger-detail-batch'] });
}

/**
 * Invalidates all analytics-related caches for a specific ledger.
 * Call this when confirmedUpdatedDate changes, as it affects:
 * - Value/return filtering (date <= confirmedUpdatedDate)
 * - FX conversion timestamps (min(periodEnd, confirmedUpdatedDate))
 * - P&L calculations
 * - Historical charts
 */
export function invalidateLedgerAnalyticsCache(queryClient: QueryClient, ledgerId: number): void {
    // Individual ledger stats (P&L, MWRR, currentValue, etc.)
    queryClient.invalidateQueries({ queryKey: ['ledger-stats', ledgerId] });
    // Ledger-specific historical analytics (values, pnl charts)
    queryClient.invalidateQueries({ queryKey: ['ledgers', ledgerId, 'analytics'] });
    // Portfolio-level aggregations (these include this ledger's data)
    queryClient.invalidateQueries({ queryKey: ['ledgers', 'analytics'] });
    // Dashboard batch analytics (net worth, projections)
    queryClient.invalidateQueries({ queryKey: ['batch-dashboard-analytics'] });
}

/**
 * Invalidates all query caches, triggering refetch of active queries.
 * Use after settings changes (like display currency) where all data needs refresh.
 */
export function invalidateAllCaches(queryClient: QueryClient): void {
    queryClient.invalidateQueries();
}
